import { Component, ojs, app, state, v, component } from "modular-openscriptjs";
import { pageInfo, scrollToTop, inform, empty, formatThousand } from "../../helpers";
import { api, PAGES, STRINGS, dc } from "../../declarations";
import SolidIcon from "../Blocks/SolidIcon";
import InfiniteScroll from "../Blocks/InfiniteScroll";
import Spinner from "../Blocks/Spinner";
import { dom } from "../../fotastart";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const h = app("h");

ojs(SolidIcon, InfiniteScroll, Spinner);

export default class Awards extends Component {
    loading = state(true);
    page = 1;
    lastPage = false;
    initialized = false;

    async init() {
        if (this.initialized) return;
        this.initialized = true;
        this.page = 1;
        this.lastPage = false;
        this.fetchAwards();
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (c) c.init();
    }

    async fetchAwards(marker) {
        let spinner = null;
        
        if (marker) {
            spinner = h.Spinner({
                class: "p-3",
                parent: marker,
                resetParent: true,
            });
        } else {
            this.loading.value = true;
        }
        
        const holder = dom.get("[hook='awards-list']");

        if (holder?.children.length === 0) {
            this.lastPage = false;
            this.page = 1;
        }

        if (this.lastPage) {
            spinner?.remove();
            return;
        }

        try {
            let payload = {
                page: this.page,
                interval: 10,
            };

            const res = await api.get("awards", payload, { silent: true });

            if (!res && marker) {
                spinner?.remove();
                return;
            }

            const awardsList = res.data.awards.data || [];

            if (empty(awardsList) && res.data.awards.current_page === 1) {
                h.div(
                    {
                        class: "d-flex flex-column align-items-center justify-content-center py-5 mt-4 bg-light rounded-4 border w-100",
                        style: "min-height: 350px; border-style: dashed !important; border-width: 2px !important;",
                        parent: holder,
                        resetParent: true,
                    },
                    h.div(
                        {
                            class: "bg-white p-4 rounded-circle shadow-sm mb-4 d-flex align-items-center justify-content-center",
                            style: "width: 80px; height: 80px;",
                        },
                        h.i({ class: "bi bi-award fs-1 text-primary" }),
                    ),
                    h.h4(
                        { class: "fw-bold text-dark mb-2" },
                        "No Awards Yet",
                    ),
                    h.p(
                        {
                            class: "text-secondary mb-4 text-center px-4",
                            style: "max-width: 400px;",
                        },
                        "Keep up the great work! Your milestone awards and achievement certificates will appear here once they are issued.",
                    )
                );
            } else {
                if (res.data.awards.current_page === 1 && holder) {
                    dom.empty(holder);
                }

                for (let award of awardsList) {
                    let id = `a-${award.id}`;
                    if (!dom.get(`[award-card-id="${id}"]`, holder)) {
                        this.renderAwardCard(award, holder);
                    }
                }

                if (this.page >= res.data.awards.last_page && holder) {
                    this.lastPage = true;
                } else {
                    h.button(
                        h.i({
                            class: "bi bi-arrow-down-circle me-2 fs-5",
                            style: "line-height: 0;",
                        }),
                        "Load More",
                        {
                            parent: marker,
                            resetParent: true,
                            class: "btn btn-outline-primary d-flex align-items-center justify-content-center mx-auto my-4 rounded-pill px-4 fw-semibold",
                            listeners: {
                                click: () => this.fetchAwards(marker),
                            },
                        },
                    );
                }
            }

            this.page = res.data.awards.current_page + 1;
        } catch (e) {
            consoleErrorOnLocal(e);
            inform("Failed to load awards", STRINGS.ERROR);
        } finally {
            this.loading.value = false;
            spinner?.remove();
        }
    }

    render(...args) {
        pageInfo(PAGES.AWARDS);
        scrollToTop();

        return h.div(
            { class: "min-vh-100 pb-5 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                h.div(
                    {
                        class: "mb-3 d-flex align-items-center justify-content-between",
                    },
                    h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.button(
                            {
                                class: "btn btn-white border shadow-sm rounded-circle p-2",
                                onclick: "history.back()",
                            },
                            h.SolidIcon("arrow-left"),
                        ),
                        h.h2({ class: "fw-bold mb-0" }, "My Awards"),
                    )
                ),

                v(this.loading, (l) => {
                    if (l.value) {
                        return h.div(
                            { class: "text-center py-5" },
                            h.div({ class: "spinner-border text-primary" }),
                        );
                    }

                    return h.InfiniteScroll({
                        child: h.div({
                            class: "row g-4 w-100 m-0",
                            hook: "awards-list",
                            uuid: "awards-list-holder"
                        }),
                        callback: (entries, observer, marker) => {
                            if (entries[0]?.isIntersecting && !this.lastPage) {
                                this.fetchAwards(marker);
                            }
                        },
                    });
                }),
            ),
            ...args
        );
    }

    renderAwardCard(award, parent) {
        h.div(
            {
                class: "col-12 col-md-6 p-0",
                "award-card-id": `a-${award.id}`,
                parent,
            },
            h.div(
                {
                    class: "card border-0 shadow-sm h-100 overflow-hidden",
                    style: "border-radius: 16px; background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%); border: 1px solid rgba(0,0,0,0.03)!important;",
                },
                h.div(
                    { class: "card-body p-4 d-flex gap-3 align-items-start" },
                    h.div(
                        {
                            class: "d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-warning bg-opacity-10 text-warning border border-2 border-warning border-opacity-25",
                            style: "width: 56px; height: 56px;",
                        },
                        h.i({ class: "bi bi-award fs-3" })
                    ),
                    h.div(
                        { class: "flex-grow-1" },
                        h.div(
                            { class: "d-flex justify-content-between align-items-start gap-2" },
                            h.h5({ class: "fw-bold mb-1 text-dark" }, award.occasion || "Award Achievement"),
                            award.capa_amount > 0
                                ? h.span(
                                      { class: "badge bg-success bg-opacity-10 text-success rounded-pill fw-bold border border-success border-opacity-10 py-1.5 px-3" },
                                      `+${dc.capaSymbol}${formatThousand(award.capa_amount)}`
                                  )
                                : h.empty()
                        ),
                        h.p({ class: "text-secondary mb-3 small", style: "line-height: 1.5; font-size: 0.9rem;" }, award.description || "Milestone reached!"),
                        h.div(
                            { class: "d-flex align-items-center text-muted small" },
                            h.i({ class: "bi bi-calendar3 me-2" }),
                            h.span({ class: "fw-medium" }, dayjs(award.created_at).fromNow())
                        )
                    )
                )
            )
        );
    }
}

ojs(Awards);
